import React, { useEffect, useMemo, useRef, useState } from "react";
import { applyLicenseKey } from "../../../../../../utils/License";
import ConfirmationModel from "../../../../../../global/components/ConfirmationModal/confirmationModel";
import { getLocationMeta } from "./projectLocationService";
import ProjectLocationCreate from "./ProjectLocationCreate";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

type FlatRow =
  | {
      rowType: "country";
      id: string;
      country_name: string;
      country: any;
      childCount: number;
      enabled: boolean;
    }
  | {
      rowType: "city";
      id: string;
      parentId: string;
      country_name: string;
      state_name: string;
      city_name: string;
      zones: string;
      enabled: boolean;
    };

const flattenLocationRows = (data: any[]): FlatRow[] => {
  const rows: FlatRow[] = [];
  (data || []).forEach((country) => {
    let childCount = 0;
    (country.states || []).forEach((state: any) => {
      childCount += (state.cities || []).length;
    });

    rows.push({
      rowType: "country",
      id: country._id,
      country_name: country.country_name,
      country,
      childCount,
      enabled: !!country.enabled,
    });

    (country.states || []).forEach((state: any) => {
      (state.cities || []).forEach((city: any) => {
        rows.push({
          rowType: "city",
          id: city._id || `${country._id}-${state._id}-${city.name}`,
          parentId: country._id,
          country_name: country.country_name,
          state_name: state.name,
          city_name: city.name,
          zones: (city.zones || []).map((z: any) => z.name).join(", "),
          enabled: !!city.enabled,
        });
      });
    });
  });
  return rows;
};

const ProjectLocationMetaList = () => {
  const [countryData, setCountryData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [countryMetaDrawer, setCountryMetaDrawer] = useState(false);
  const [countryDrawerData, setCountryDrawerData] = useState<any>({});
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  const setStateDataCompat = (newData: any) => {
    if ("countryDrawerData" in newData) {
      setCountryDrawerData(newData.countryDrawerData);
    }
    if ("countryMetaDrawer" in newData) {
      setCountryMetaDrawer(!!newData.countryMetaDrawer);
    }
    if ("selectedRow" in newData) setSelectedRow(newData.selectedRow);
    if ("dialogOpen" in newData) setDialogOpen(!!newData.dialogOpen);
  };

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    if (!countryMetaDrawer) {
      handleTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, countryMetaDrawer]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleTableData = async () => {
    try {
      setLoading(true);
      const response = await getLocationMeta(search);
      const data = response.data || [];
      setCountryData(data);

      setExpanded((prev) => {
        const next: Record<string, boolean> = {};
        data.forEach((c: any) => {
          next[c._id] = prev[c._id] ?? true;
        });
        return next;
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const flatRows = useMemo(
    () => flattenLocationRows(countryData),
    [countryData]
  );

  const visibleRows = useMemo(() => {
    return flatRows.filter((row) => {
      if (row.rowType === "country") return true;
      return !!expanded[row.parentId];
    });
  }, [flatRows, expanded]);

  const closeDrawer = () => {
    setCountryMetaDrawer(false);
    setCountryDrawerData({});
    setSelectedRow(null);
  };

  const openCreate = () => {
    setCountryDrawerData({ states: [], enabled: false });
    setSelectedRow(null);
    setCountryMetaDrawer(true);
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Country",
      field: "country_name",
      minWidth: 220,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatRow;
        if (row.rowType === "country") {
          const isOpen = !!expanded[row.id];
          const hasChildren = row.childCount > 0;
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`flex h-6 w-6 items-center justify-center rounded text-[#6B7280] ${
                  hasChildren ? "hover:bg-[#F3F4F6]" : "invisible"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) {
                    setExpanded((prev) => ({
                      ...prev,
                      [row.id]: !prev[row.id],
                    }));
                  }
                }}
              >
                <Chevron open={isOpen} />
              </button>
              <span className="text-sm font-semibold text-[#111827]">
                {row.country_name || "—"}
              </span>
            </div>
          );
        }
        return <span className="pl-8 text-sm text-[#9CA3AF]">—</span>;
      },
    },
    {
      headerName: "State",
      field: "state_name",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatRow;
        if (row.rowType !== "city") return null;
        return (
          <span className="text-sm text-[#374151]">{row.state_name || "—"}</span>
        );
      },
    },
    {
      headerName: "City",
      field: "city_name",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatRow;
        if (row.rowType !== "city") return null;
        return (
          <span className="text-sm font-medium text-[#111827]">
            {row.city_name || "—"}
          </span>
        );
      },
    },
    {
      headerName: "Zones",
      field: "zones",
      minWidth: 220,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatRow;
        if (row.rowType !== "city") return null;
        return (
          <span className="line-clamp-2 text-sm text-[#6B7280]">
            {row.zones || "—"}
          </span>
        );
      },
    },
    {
      headerName: "Website",
      field: "enabled",
      minWidth: 110,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatRow;
        const on = !!row.enabled;
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              on ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#F3F4F6] text-[#6B7280]"
            }`}
          >
            {on ? "On" : "Off"}
          </span>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatRow;
        if (row.rowType !== "country") return null;
        return (
          <button
            type="button"
            data-action-button="true"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            onClick={(e) => {
              e.stopPropagation();
              const rect = (
                e.currentTarget as HTMLButtonElement
              ).getBoundingClientRect();
              setMenuPos({ top: rect.bottom + 4, left: rect.left - 160 });
              setSelectedRow(row.country);
              setMenuOpen(true);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
        );
      },
    },
  ];

  const createState = {
    countryDrawerData,
    countryMetaDrawer,
    selectedRow,
    dialogOpen,
    search,
  };

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Project Location List
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage countries and cities. Only items with Website = On appear on yhataw.com.
          </p>
        </div>
        <YhButton variant="primary" size="sm" onClick={openCreate}>
          + Create
        </YhButton>
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder="Search by country, state and city"
          value={search}
          type="search"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={visibleRows}
          getRowId={(row) => row.id}
          loading={loading}
          emptyMessage="No locations found"
          className="h-full"
          showPagination={false}
        />
      </div>

      {menuOpen && menuPos ? (
        <div
          ref={menuRef}
          className="fixed z-[1500] w-48 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase text-[#9CA3AF]">
            Actions
          </p>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
            onClick={() => {
              setCountryDrawerData(selectedRow || { states: [] });
              setCountryMetaDrawer(true);
              setMenuOpen(false);
            }}
          >
            Edit
          </button>
        </div>
      ) : null}

      {countryMetaDrawer ? (
        <ProjectLocationCreate
          state={createState}
          setStateData={setStateDataCompat}
          closeDrawer={closeDrawer}
        />
      ) : null}

      <ConfirmationModel
        open={dialogOpen}
        title="Confirmation"
        description="Are you sure you want to delete?"
        onConfirm={() => setDialogOpen(false)}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default ProjectLocationMetaList;
